import { DocumentNode, gql } from '@apollo/client';
import { useRouter } from 'next/router';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MembershipTermLifecycleStatus } from '@/graphql-types/globalTypes';

import { PrePurchaseConfirmationModal_UserFragment } from './graphql-types/PrePurchaseConfirmationModal_UserFragment';

export const PrePurchaseConfirmationModal: React.FC<{
  user?: PrePurchaseConfirmationModal_UserFragment;
  isOpen: boolean;
  onClose: () => void;
  membershipTypeId: string | undefined;
}> & {
  userFragment: DocumentNode;
} = ({ user, isOpen, onClose, membershipTypeId }) => {
  const router = useRouter();

  if (!user) {
    return null;
  }

  const hasUpcomingMembership = !!user.membershipData?.nextMembershipTerm;

  const isCurrentMembershipTermInRenewalPeriod =
    user.membershipData?.currentMembershipTerm?.lifecycleStatus ===
    MembershipTermLifecycleStatus.RENEWING;

  const redirectToCheckout = async () => {
    if (!membershipTypeId) {
      throw new Error('Unexpected Missing Membership Type ID');
    }

    await router.push(`/new-membership/${membershipTypeId}`);
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        isCurrentMembershipTermInRenewalPeriod
          ? 'Are you sure you want to switch your membership?'
          : 'Are you sure you want to purchase a new membership?'
      }
      body={
        isCurrentMembershipTermInRenewalPeriod
          ? hasUpcomingMembership
            ? `Doing this means your current membership will run its course and your queued membership will be replaced with the new membership you've selected.`
            : `Doing this means your current membership will run its course and a new membership will start with the new membership type you've selected.`
          : hasUpcomingMembership
          ? 'Doing this will terminate your current and queued membership and start a new membership term for which you will pay in full.'
          : 'Doing this will terminate your current membership and start a new membership term for which you will pay in full.'
      }
      confirmButtonText="Continue"
      onConfirm={redirectToCheckout}
      shouldCloseOnConfirmation={false}
    />
  );
};

PrePurchaseConfirmationModal.userFragment = gql`
  fragment PrePurchaseConfirmationModal_UserFragment on User {
    _id
    membershipData {
      currentMembershipTerm {
        _id
        lifecycleStatus
      }
      nextMembershipTerm {
        _id
      }
    }
  }
`;
